import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { ExportJob } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { toZonedDateKey } from '@nucleus-commerce/core';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { saveFile } from '@/shared/browser/save-file';

import { exportsApi } from '../api/exports-api';
import { fallbackExportFilename } from '../schemas/job-rules';

/**
 * Fetched through the API client and saved from memory: a plain link to the endpoint would not
 * send the client header the cookie session requires. The backend refuses with 409 while the job
 * is not done (or failed) and 404 once the file expired; those get their own words.
 */
export function useDownloadExport(storeId: string) {
  const { t } = useTranslation('importsExports');
  const message = useErrorMessage();
  return useMutation({
    mutationFn: async (job: ExportJob) => {
      const file = await exportsApi.download(storeId, job.id);
      const day = toZonedDateKey(new Date(), 'UTC');
      saveFile(file.filename ?? fallbackExportFilename(job.kind, day), file.blob);
    },
    meta: { silentError: true },
    onError: (error) => {
      if (isApiError(error) && error.status === 409) {
        toast.error(t(error.meta.error ? 'download.failed' : 'download.notReady'));
      } else if (isApiError(error) && error.status === 404) {
        toast.error(t('download.expired'));
      } else {
        toast.error(message(error));
      }
    },
  });
}
